/**
 * "What happened" for a failed or partial scan (DEC-197, UI_SDD §R.13), opened from the
 * Monitor's red box and from "See why" on the Scans dashboard. A summary sentence, a
 * timeline with each wave's run time and where it stopped, every recorded problem
 * grouped by agent (each opens the agent pop-up), and what to do.
 *
 * Reads `GET /scans/{id}/what-happened`. Against a server without it, or for a scan
 * before the details were recorded, it shows what the scan row saved: the full errors
 * list, grouped, with one line saying the rest was not recorded. Never a guess.
 */
import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { scansApi } from '../../api/scans'
import { savedRowToAgent } from '../../stores/scanStore'
import type { ScanStatusResponse, WhatHappenedResponse, WhatHappenedStep } from '../../types/api'
import { AGENT_LABELS } from '../../types/api'
import { dateTimeLabel, durationLabel } from '../../utils/format'
import { Modal } from '../shared/Modal'
import { AgentPopup } from '../monitor/AgentPopup'
import { tokenColor } from '../monitor/waveModel'
import { KIND_HEADLINE } from '../monitor/failureModel'
import { NOT_RECORDED_LINE, fromScanRow } from './whatHappenedModel'

interface Props {
  scan: ScanStatusResponse
  onClose: () => void
  /** Run again / Upload again / New scan: the remedy buttons the caller already has. */
  actions?: ReactNode
}

function stepTone(step: WhatHappenedStep): string {
  switch (step.status) {
    case 'failed':
      return 'mark-critical'
    case 'problems':
      return 'mark-warn'
    case 'stopped':
    case 'skipped':
      return 'mark-idle'
    default:
      return step.kind === 'queued' || step.kind === 'started' ? 'mark-running' : 'mark-success'
  }
}

function isStopPoint(step: WhatHappenedStep): boolean {
  return step.status === 'failed' || step.kind === 'failed' || step.kind === 'interrupted'
}

export function WhatHappenedPopup({ scan, onClose, actions }: Props) {
  const [agentId, setAgentId] = useState<string | null>(null)
  const story = useQuery({
    queryKey: ['what-happened', scan.scan_id],
    queryFn: () => scansApi.whatHappened(scan.scan_id),
    retry: false,
    staleTime: 60_000,
  })
  const agents = useQuery({
    queryKey: ['scan-agents', scan.scan_id],
    queryFn: () => scansApi.agents(scan.scan_id),
    enabled: agentId !== null,
    retry: false,
    staleTime: Infinity,
  })

  const data: WhatHappenedResponse | null = story.data ?? (story.isError ? fromScanRow(scan) : null)

  if (agentId) {
    const row = agents.data?.agents.find((r) => r.agent_id === agentId)
    if (row) {
      return (
        <AgentPopup
          agent={savedRowToAgent(row)}
          scanId={scan.scan_id}
          finished
          fromSaved
          onClose={() => setAgentId(null)}
        />
      )
    }
  }

  const failed = scan.state === 'failed'
  const problemAgents = data?.agents ?? []
  const scanErrors = data?.scan_errors ?? []

  return (
    <Modal
      title={failed ? 'What happened' : 'What happened, and what is missing'}
      subtitle={
        data && typeof data.run_seconds === 'number'
          ? `Ran ${data.run_time_is_partial ? 'at least ' : ''}${durationLabel(data.run_seconds)}`
          : undefined
      }
      dot={tokenColor(failed ? 'mark-critical' : 'mark-warn')}
      onClose={onClose}
      maxWidth={620}
      testId="what-happened"
    >
      {!data ? (
        <p className="text-[13px] text-muted m-0">Loading what the scan recorded…</p>
      ) : (
        <div className="flex flex-col gap-5 text-[13px] leading-[1.55] text-body">
          <p className="m-0 text-[14px] text-ink" data-testid="what-happened-summary">
            {data.summary}
          </p>

          {data.timeline.length > 0 && (
            <section aria-label="Timeline">
              <span className="font-semibold text-[10px] tracking-[0.16em] uppercase text-muted">Timeline</span>
              <ol className="m-0 mt-2 p-0 list-none flex flex-col" data-testid="what-happened-timeline">
                {data.timeline.map((step, i) => {
                  const last = i === data.timeline.length - 1
                  const stopped = isStopPoint(step)
                  return (
                    <li key={`${step.kind}-${i}`} className="relative flex gap-3 pb-3">
                      {!last && <span aria-hidden className="absolute left-[4px] top-[14px] bottom-0 w-px bg-hairline" />}
                      <span
                        aria-hidden
                        className="mt-[6px] w-[9px] h-[9px] rounded-full flex-none"
                        style={{ background: tokenColor(stepTone(step)) }}
                      />
                      <span className="flex flex-col min-w-0">
                        <span className={`font-semibold ${stopped ? 'text-critical' : 'text-ink'}`}>
                          {step.label}
                          {typeof step.run_seconds === 'number' && (
                            <span className="font-normal text-muted tabular-nums"> · {durationLabel(step.run_seconds)}</span>
                          )}
                        </span>
                        {step.at && <span className="text-[12px] text-muted">{dateTimeLabel(step.at)}</span>}
                        {step.detail && <span className="text-[12.5px]">{step.detail}</span>}
                      </span>
                    </li>
                  )
                })}
              </ol>
            </section>
          )}

          {(problemAgents.length > 0 || scanErrors.length > 0) && (
            <section aria-label="Problems by agent" className="flex flex-col gap-2">
              <span className="font-semibold text-[10px] tracking-[0.16em] uppercase text-muted">Problems, by agent</span>
              <ul className="m-0 p-0 list-none flex flex-col gap-2" data-testid="what-happened-problems">
                {scanErrors.length > 0 && (
                  <li>
                    <div className="rounded-md border border-hairline px-3 py-2">
                      <span className="font-semibold text-ink">The scan itself</span>
                      {scanErrors.map((m, i) => (
                        <span key={`${i}-${m}`} className="block text-[12.5px]">
                          {m}
                        </span>
                      ))}
                    </div>
                  </li>
                )}
                {problemAgents.map((g) => {
                  const head = g.failure ? KIND_HEADLINE[g.failure.kind] : null
                  const lines = g.failure?.message ? [g.failure.message] : g.problems.length > 0 ? g.problems : g.errors
                  return (
                    <li key={g.agent_id}>
                      <button
                        type="button"
                        onClick={() => setAgentId(g.agent_id)}
                        aria-haspopup="dialog"
                        className="w-full text-left rounded-md border border-hairline px-3 py-2 hover:border-hover-edge hover:bg-hover-hue focus:outline-none focus-visible:shadow-focus"
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-semibold text-ink">{g.label || AGENT_LABELS[g.agent_id] || g.agent_id}</span>
                          {head && <span className="text-muted">· {head}</span>}
                          <ChevronRight aria-hidden className="ml-auto w-3 h-3 text-muted" />
                        </span>
                        {lines.slice(0, 3).map((m, i) => (
                          <span key={`${i}-${m}`} className="block text-[12.5px]">
                            {m}
                          </span>
                        ))}
                        {lines.length > 3 && <span className="block text-[12px] text-muted">and {lines.length - 3} more</span>}
                      </button>
                    </li>
                  )
                })}
              </ul>
              {agentId && agents.isLoading && <p className="m-0 text-[12px] text-muted">Opening…</p>}
              {agentId && agents.isError && (
                <p className="m-0 text-[12px] text-critical">The agent details could not be loaded.</p>
              )}
            </section>
          )}

          {(data.failure_remedy || actions) && (
            <section aria-label="What to do" className="flex flex-col gap-2">
              <span className="font-semibold text-[10px] tracking-[0.16em] uppercase text-muted">What to do</span>
              {data.failure_remedy && <p className="m-0">{data.failure_remedy}</p>}
              {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
            </section>
          )}

          {!data.recorded_details && (
            <p className="m-0 text-[12px] text-muted" data-testid="not-recorded-line">
              {NOT_RECORDED_LINE}
            </p>
          )}
        </div>
      )}
    </Modal>
  )
}
